import type {History, Location} from 'history';

import {baseContext, PEEK_PARAM} from '../../../context';

type RouteContext = Pick<typeof baseContext, 'evaluationsUIUrl'>;

const formatSelector = (name: string, version?: string | null) => {
  return version ? `${name}:${version}` : name;
};

export const navigateToUrlPreservingPeek = (
  history: History,
  location: Location,
  targetUrl: string
): void => {
  let currentPeekPath: string | null = null;
  if (typeof window !== 'undefined') {
    const rootSearchParams = new URLSearchParams(window.location.search);
    currentPeekPath = rootSearchParams.get(PEEK_PARAM);
  } else {
    const localSearchParams = new URLSearchParams(location.search);
    currentPeekPath = localSearchParams.get(PEEK_PARAM);
  }

  const urlParts = targetUrl.split('?');
  const newPathname = urlParts[0];
  const newQueryString = urlParts[1] || '';

  const newSearchParams = new URLSearchParams(newQueryString);
  if (currentPeekPath) {
    newSearchParams.set(PEEK_PARAM, currentPeekPath);
  }

  const nextSearchString = newSearchParams.toString();

  history.push({
    pathname: newPathname,
    search: nextSearchString ? `?${nextSearchString}` : '',
  });
};

export const buildEvaluationsUrlWithDatasetSelector = (
  routeContext: RouteContext,
  entity: string,
  project: string,
  datasetName: string,
  datasetVersion?: string | null
): string => {
  const params = new URLSearchParams();
  params.set(
    'datasetSelector',
    JSON.stringify(formatSelector(datasetName, datasetVersion))
  );
  const baseUrl = routeContext.evaluationsUIUrl(entity, project);
  return `${baseUrl}?${params.toString()}`;
};

export const buildEvaluationsUrlWithEvaluationSelector = (
  routeContext: RouteContext,
  entity: string,
  project: string,
  evaluationName: string,
  evaluationVersion?: string | null
): string => {
  const params = new URLSearchParams();
  params.set(
    'evaluationSelector',
    JSON.stringify(formatSelector(evaluationName, evaluationVersion))
  );
  const baseUrl = routeContext.evaluationsUIUrl(entity, project);
  return `${baseUrl}?${params.toString()}`;
};

export const buildEvaluationsUrlWithModelFilter = (
  routeContext: RouteContext,
  entity: string,
  project: string,
  modelRef: string
): string => {
  const params = new URLSearchParams();
  const filter = {
    inputObjectVersionRefs: [modelRef],
  };
  params.set('filter', JSON.stringify(filter));
  const baseUrl = routeContext.evaluationsUIUrl(entity, project);
  return `${baseUrl}?${params.toString()}`;
};
